import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams } from 'react-router'
import { useDispatch } from 'react-redux'
import { criarAlerta } from 'actions'
import useRouter from 'utils/useRouter'
import { useExameRealizadoService } from 'hooks/useExameRealizadoService'
import { useVestigioService } from 'hooks/useVestigioService'
import { useExameCoincidenciaPerfisService } from 'hooks/useExameCoincidenciaPerfisService'

export const useExameCoincidencia = () => {
  const params = useParams()
  const dispatch = useDispatch()
  const router = useRouter()

  const exameRealizadoService = useExameRealizadoService()
  const vestigioService = useVestigioService()
  const exameCoincidenciaPerfisService = useExameCoincidenciaPerfisService()

  const [processandoAlteracaoRespostas, setProcessandoAlteracaoRespostas] = useState(false)
  const [processandoOperacao, setProcessandoOperacao] = useState(false)

  const [formValues, setFormValues] = useState({
    idExame: params.idExameRealizado,
    casoA: '',
    elementoA: '',
    nomeCadaverA: '',
    origemA: '',
    casoB: '',
    elementoB: '',
    nomeCadaverB: '',
    origemB: '',
    numLaudo: '',
  })

  const [touched, setTouched] = useState({})

  const errors = useMemo(() => {
    const errs = {}
    if (touched.casoA && !formValues.casoA) errs.casoA = 'Campo obrigatório'
    if (touched.elementoA && !formValues.elementoA) errs.elementoA = 'Campo obrigatório'
    if (touched.casoB && !formValues.casoB) errs.casoB = 'Campo obrigatório'
    if (touched.elementoB && !formValues.elementoB) errs.elementoB = 'Campo obrigatório'
    if (touched.numLaudo && !formValues.numLaudo) errs.numLaudo = 'Campo obrigatório'
    return errs
  }, [formValues, touched])

  const isFormValid = useMemo(() => {
    return !!(
      formValues.casoA &&
      formValues.elementoA &&
      formValues.casoB &&
      formValues.elementoB &&
      formValues.numLaudo
    )
  }, [formValues])

  const hasError = useCallback((field) => {
    return !!errors[field]
  }, [errors])

  const fetchTiposVestigios = useCallback(async () => {
    try {
      await vestigioService.listarTiposVestigiosAsync(setProcessandoOperacao)
    } catch (error) {
      console.error('Falha ao carregar tipos de vestígios:', error)
    }
  }, [])

  const fetchTiposVestigiosEspecificos = useCallback(async () => {
    try {
      await vestigioService.listarTiposVestigiosEspecificosAsync(setProcessandoOperacao)
    } catch (error) {
      console.error('Falha ao carregar tipos de vestígios específicos:', error)
    }
  }, [])

  const fetchExameCoincidencia = useCallback(async () => {
    if (params.idExameRealizado) {
      try {
        const response = await exameCoincidenciaPerfisService.listarExameCoincidenciaPerfisAsync(params.idExameRealizado)
        if (!response) {
          dispatch(criarAlerta({ status: 501 }, router, `/casos/caso/solicitacao/exameSolicitado/detalhar/${params.idExameSolicitado}`))
        }
      } catch (error) {
        console.error('Falha ao listar exame de coincidência de perfis:', error)
      }
    }
  }, [params.idExameRealizado, params.idExameSolicitado, dispatch, router])

  useEffect(() => {
    fetchTiposVestigios()
  }, [fetchTiposVestigios])

  useEffect(() => {
    fetchTiposVestigiosEspecificos()
  }, [fetchTiposVestigiosEspecificos])

  useEffect(() => {
    fetchExameCoincidencia()
  }, [fetchExameCoincidencia])

  useEffect(() => {
    const localForm = JSON.parse(localStorage.getItem('@sigep/formCP') || '{}')
    if (localForm && localForm.idExame === params.idExameRealizado) {
      setFormValues((prev) => ({
        ...prev,
        ...localForm,
      }))
    } else {
      localStorage.removeItem('@sigep/formCP')
    }
  }, [params.idExameRealizado])

  const handleFinalizarExame = useCallback(async () => {
    try {
      localStorage.setItem('@sigep/formCP', JSON.stringify(formValues))
      await exameRealizadoService.gerarDocumento(params.idExameRealizado, setProcessandoAlteracaoRespostas, formValues)
      router.history.push({
        pathname: `/casos/caso/solicitacao/exameSolicitado/editorExameRealizado/${params.idExameRealizado}`,
        state: { IgnoneUploadImagem: true, tipoExame: 'COINCIDENCIA_PERFIS_BALISTICO' },
      })
    } catch (error) {
      console.error('Falha ao finalizar o exame:', error)
    }
  }, [formValues, params.idExameRealizado, router.history])

  // Handlers com validações regex
  const handleCasoChange = useCallback((e) => {
    const { name, value } = e.target
    const regex = /^[a-zA-Z0-9\-\.]*$/
    if (regex.test(value) || value === '') {
      setFormValues((prev) => ({
        ...prev,
        [name]: value,
      }))
      setTouched((prev) => ({
        ...prev,
        [name]: true,
      }))
    }
  }, [])

  const handleElementoChange = useCallback((e) => {
    const { name, value } = e.target
    const regex = /^[A-Za-z]{1,3}\d{0,3}$/
    if (regex.test(value) || value === '') {
      setFormValues((prev) => ({
        ...prev,
        [name]: value,
      }))
      setTouched((prev) => ({
        ...prev,
        [name]: true,
      }))
    }
  }, [])

  const handleNomeCadaverChange = useCallback((e) => {
    const { name, value } = e.target

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }))
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }))

  }, [])

  const handleNumLaudoChange = useCallback((e) => {
    const { name, value } = e.target
    const regex = /^[a-zA-Z0-9\-]*$/
    if (regex.test(value) || value === '') {
      setFormValues((prev) => ({
        ...prev,
        [name]: value.toUpperCase(),
      }))
      setTouched((prev) => ({
        ...prev,
        [name]: true,
      }))
    }
  }, [])

  const handleRadioChange = useCallback((e) => {
    const { name, value } = e.target
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }))
  }, [])

  const handleVoltar = useCallback(() => {
    router.history.goBack()
  }, [router.history])

  return {
    formValues,
    errors,
    isFormValid,
    hasError,
    processandoAlteracaoRespostas,
    handleFinalizarExame,
    handleCasoChange,
    handleElementoChange,
    handleNomeCadaverChange,
    handleNumLaudoChange,
    handleRadioChange,
    handleVoltar,
  }
}
